import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { APP_ROUTES } from '@core/constants/app-routes';
import { ROLE_CODES } from '@core/constants/role-codes';
import { OrganizationContextService } from '@core/services/organization-context.service';
import { MasterContextService } from '@core/services/master-context.service';
import { SettingsContextService } from '@core/services/settings-context.service';
import { AuthActions } from '@auth/store/auth.actions';
import { AuthStorageService } from '@auth/services/auth-storage.service';
import { AppTheme, NavSection } from '@shared/models/layout.model';
import { DEFAULT_THEME } from '@theme/themes';

@Component({
  selector: 'app-root',
  templateUrl: './app.html',
  standalone: false,
  styleUrl: './app.css'
})
export class App implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly authStorage = inject(AuthStorageService);
  private readonly organizationContext = inject(OrganizationContextService);
  private readonly masterContext = inject(MasterContextService);
  private readonly settingsContext = inject(SettingsContextService);

  showShell = true;
  menuOpen = false;
  readonly currentDate = new Date();
  activeTheme: AppTheme = DEFAULT_THEME;

  get userName(): string {
    const user = this.authStorage.getUser();
    return user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.name || 'User' : 'User';
  }

  get userRole(): string {
    return this.authStorage.getUser()?.role?.value ?? 'Staff';
  }

  /** Sidebar, grouped as in the Clinical Ease design. `comingSoon` = page not migrated yet.
   *  Built into a field (not a getter) so the child component gets a stable reference; rebuilt on navigation. */
  sections: NavSection[] = this.buildSections();

  private buildSections(): NavSection[] {
    const doctorId = this.authStorage.getUser()?.doctorId;
    const isSysAdmin = this.authStorage.getUser()?.role?.key === ROLE_CODES.SysAdmin;
    return [
      {
        title: 'Main',
        items: [
          { label: 'Dashboard', icon: 'grid_view', route: APP_ROUTES.dashboard },
          { label: 'Registration / Patients', icon: 'patient_list', route: APP_ROUTES.patients },
        ],
      },
      {
        title: 'Doctor Management',
        items: [
          { label: 'Doctors Directory', icon: 'badge', route: APP_ROUTES.doctors.root, exact: true },
          {
            label: 'My Work Queue',
            icon: 'clinical_notes',
            route: doctorId ? APP_ROUTES.doctors.mywork(doctorId) : undefined,
            comingSoon: !doctorId,
          },
        ],
      },
      {
        title: 'Clinical & Appointments',
        items: [
          { label: 'Appointments', icon: 'calendar_month', route: APP_ROUTES.appointments.root, exact: true },
          { label: 'Appointment Settings', icon: 'tune', route: APP_ROUTES.appointments.settings },
          { label: 'OPD Billing & Payments', icon: 'receipt_long', route: APP_ROUTES.billing.root },
        ],
      },
      {
        title: 'Pharmacy',
        items: [
          { label: 'Dispensing & Bills', icon: 'medication', route: APP_ROUTES.pharmacy.billing },
          { label: 'Inventory & Batch', icon: 'inventory_2', route: APP_ROUTES.pharmacy.inventory },
          { label: 'Purchases', icon: 'shopping_cart', route: APP_ROUTES.pharmacy.purchases },
          { label: 'Pharmacy Settings', icon: 'tune', route: APP_ROUTES.pharmacy.settings },
          {
            label: 'Pharmacy Masters',
            icon: 'category',
            children: [
              { label: 'Medicines', icon: 'medication', route: APP_ROUTES.pharmacy.medicines },
              { label: 'Categories', icon: 'category', route: APP_ROUTES.pharmacy.categories },
              { label: 'Manufacturers', icon: 'factory', route: APP_ROUTES.pharmacy.manufacturers },
              { label: 'Suppliers', icon: 'local_shipping', route: APP_ROUTES.pharmacy.suppliers },
              { label: 'Batch Records', icon: 'inventory_2', route: APP_ROUTES.pharmacy.batchRecords },
            ],
          },
        ],
      },
      {
        title: 'Payroll',
        items: [
          {
            label: 'Payroll',
            icon: 'payments',
            children: [
              { label: 'Overview', icon: 'space_dashboard', route: APP_ROUTES.payroll.root },
              { label: 'Employees', icon: 'badge', route: APP_ROUTES.payroll.employees },
              { label: 'Offer Letters', icon: 'description', route: APP_ROUTES.payroll.offerLetters },
              { label: 'Internship Certificates', icon: 'workspace_premium', route: APP_ROUTES.payroll.internshipCertificates },
              { label: 'Salary Structure', icon: 'payments', route: APP_ROUTES.payroll.salaryStructures },
              { label: 'Employee Salary Structure', icon: 'account_balance_wallet', route: APP_ROUTES.payroll.employeeSalaryStructures },
              { label: 'Separation', icon: 'logout', route: APP_ROUTES.payroll.separation },
              { label: 'Settings', icon: 'settings', route: APP_ROUTES.payroll.settings },
            ],
          },
        ],
      },
      {
        title: 'Administration',
        items: [
          { label: 'Users & Staff', icon: 'group', route: '/organization/users' },
          {
            label: 'Organization Management',
            icon: 'apartment',
            children: [
              { label: 'Organization Details', icon: 'domain', route: `${APP_ROUTES.organization}/general` },
              { label: 'Locations', icon: 'location_on', route: `${APP_ROUTES.organization}/locations` },
              { label: 'Stations', icon: 'local_hospital', route: `${APP_ROUTES.organization}/stations` },
              { label: 'App Constants', icon: 'tune', route: `${APP_ROUTES.organization}/app-constants` },
              // the modules an organization opted for (and is billed for) are set by the application administrator, not by the organization
              ...(isSysAdmin ? [{ label: 'Module Permissions', icon: 'extension', route: `${APP_ROUTES.organization}/module-permissions` }] : []),
              { label: 'Roles & Permissions', icon: 'admin_panel_settings', route: `${APP_ROUTES.organization}/user-roles` },
              { label: 'Doctor Mapping', icon: 'stethoscope', route: `${APP_ROUTES.organization}/doctor-mapping` },
              { label: 'Specialization Mapping', icon: 'account_tree', route: `${APP_ROUTES.organization}/specialization-mapping` },
              { label: 'File Explorer', icon: 'folder_open', route: `${APP_ROUTES.organization}/file-explorer` },
            ],
          },
          { label: 'Service Catalog', icon: 'medical_services', route: '/organization/services' },
          { label: 'Bulk Import', icon: 'upload_file', route: APP_ROUTES.import },
        ],
      },
      {
        title: 'User',
        items: [
          { label: 'Profile', icon: 'account_circle', route: APP_ROUTES.profile },
          { label: 'User Settings', icon: 'settings', route: APP_ROUTES.settings },
        ],
      },
      {
        title: 'Support',
        items: [{ label: 'Help & Documentation', icon: 'help', comingSoon: true }],
      },
    ];
  }

  ngOnInit(): void {
    // a page refresh or a direct link: load the organization of the saved session again (only login did this, so pages that read it stayed empty)
    const savedUser = this.authStorage.getUser();
    if (savedUser && this.authStorage.isTokenValid()) this.organizationContext.setTenant(savedUser.tenantId ?? null);
    this.initTheme();
    this.settingsContext.loadForCurrentUser();
    this.updateLayoutByRoute(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.settingsContext.loadForCurrentUser(); // after a login: the user's saved settings, from the server
        this.sections = this.buildSections(); // picks up the signed-in user's doctorId after login
        this.updateLayoutByRoute(event.urlAfterRedirects);
        this.menuOpen = false;
      });
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  closeMenu(): void {
    this.menuOpen = false;
  }

  logout(): void {
    this.authStorage.clearSession();
    this.organizationContext.clear();
    this.masterContext.clear();
    this.settingsContext.clearOnSignOut();
    this.store.dispatch(AuthActions.logout());
    void this.router.navigateByUrl(APP_ROUTES.auth.login);
  }

  /** The theme picked in the navbar is a user setting: applied now and saved for the user. */
  onThemeChange(theme: AppTheme): void {
    this.settingsContext.updateTheme(theme === 'theme-blue' ? 'blue' : 'light');
    this.settingsContext.saveSettings().subscribe();
  }

  navigateToProfile(): void {
    void this.router.navigate([APP_ROUTES.profile]);
  }

  navigateToSettings(): void {
    void this.router.navigate([APP_ROUTES.settings]);
  }

  /** The navbar's theme picker follows the user's settings (also when they are loaded from the server). */
  private initTheme(): void {
    this.settingsContext.settings$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((settings) => (this.activeTheme = settings.theme === 'blue' ? 'theme-blue' : DEFAULT_THEME));
  }

  private updateLayoutByRoute(url: string): void {
    this.showShell = !url.startsWith(APP_ROUTES.auth.root);
  }
}
